import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import clsx from 'clsx';
import SystemBanner from './SystemBanner';
import PromoBanner from './PromoBanner';
import RepFirmBanner from './RepFirmBanner';
import { api } from '@/services/api';
import { loadThemePref, saveThemePrefLocal, normalizePref, resolveMode, oppositeModePref } from '@/utils/theme';

// Hamburger menu icon
const HamburgerIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Close icon for mobile menu
const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Sun icon for light mode
const SunIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

// Moon icon for dark mode
const MoonIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

interface NavLinkProps {
  alwaysHighlight?: boolean;
  to: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
}

function NavLink({ to, children, icon, onClick, alwaysHighlight }: NavLinkProps) {
  const location = useLocation();
  const isActive = to === '/'
    ? location.pathname === '/'
    : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      onClick={onClick}
      className={clsx(
        'flex items-center gap-3 px-4 py-2 rounded-lg transition-colors min-h-[44px]',
        (isActive || alwaysHighlight)
          ? 'bg-keder-yellow text-black font-medium'
          : 'text-[var(--sidebar-text)] hover:bg-[var(--bg-hover)] hover:text-[var(--sidebar-text-hover)]'
      )}
    >
      {icon && <span className="w-5 h-5 flex-shrink-0">{icon}</span>}
      <span className="nav-text">{children}</span>
    </Link>
  );
}

// Icons
const DashboardIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const OrdersIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const NewOrderIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const EstimateIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const PriceBuilderIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const DesignerIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
  </svg>
);

const EmailIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const PartsIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
);

const WarrantyIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const SupportIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const LeadsIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const CalendarIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SettingsIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const AdminIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const CommissionIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrophyIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const KEDER_LOGO_URL = 'https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png';

// Sidebar content component (shared between desktop and mobile)
function SidebarContent({
  user,
  warrantyMenuOpen,
  setWarrantyMenuOpen,
  adminMenuOpen,
  setAdminMenuOpen,
  supportMenuOpen,
  setSupportMenuOpen,
  supportUnread,
  onNavClick,
  handleLogout
}: {
  user: any;
  warrantyMenuOpen: boolean;
  setWarrantyMenuOpen: (open: boolean) => void;
  adminMenuOpen: boolean;
  setAdminMenuOpen: (open: boolean) => void;
  supportMenuOpen: boolean;
  setSupportMenuOpen: (open: boolean) => void;
  supportUnread?: number;
  onNavClick?: () => void;
  handleLogout: () => void;
}) {
  const location = useLocation();
  const dealerLogo = user?.logoUrl || KEDER_LOGO_URL;
  const dealerName = user?.dealerName || user?.companyName || 'Keder Screens';

  return (
    <>
      {/* Logo — dealer white-label or Keder default */}
      <div className="p-4 border-b border-[var(--border-color)] flex items-center gap-3">
        <img
          src={dealerLogo}
          alt={dealerName}
          style={{ maxHeight: '56px', maxWidth: '220px', width: 'auto', objectFit: 'contain' }}
          onError={(e) => { (e.target as HTMLImageElement).src = KEDER_LOGO_URL; }}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        <NavLink to="/" icon={<DashboardIcon />} onClick={onNavClick}>
          Dashboard
        </NavLink>
        <NavLink to="/invoices" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} onClick={onNavClick}>
          My Invoices
        </NavLink>
        <NavLink to="/orders" icon={<OrdersIcon />} onClick={onNavClick}>
          My Orders
        </NavLink>
        <NavLink to="/shipments" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1" /></svg>} onClick={onNavClick}>
          My Shipments
        </NavLink>
        <NavLink to="/installs" icon={<CalendarIcon />} onClick={onNavClick}>
          Calendar
        </NavLink>
        <NavLink to="/leads" icon={<LeadsIcon />} onClick={onNavClick}>
          My Leads
        </NavLink>
        <NavLink to="/orders/new" icon={<NewOrderIcon />} onClick={onNavClick} alwaysHighlight>
          New Order
        </NavLink>
        <NavLink to="/parts" icon={<PartsIcon />} onClick={onNavClick}>
          Parts & Accessories
        </NavLink>
        <NavLink to="/quote" icon={<EstimateIcon />} onClick={onNavClick}>
          Quick Estimate
        </NavLink>
        <NavLink to="/price-builder" icon={<PriceBuilderIcon />} onClick={onNavClick}>
          Price Builder
        </NavLink>
        <NavLink to="/designer" icon={<DesignerIcon />} onClick={onNavClick}>
          Screen Designer
        </NavLink>
        {/* v2: Visual Order
        <NavLink to="/visual-order" icon={<DesignerIcon />} onClick={onNavClick}>
          Visual Order
        </NavLink> */}
        <NavLink to="/email-templates" icon={<EmailIcon />} onClick={onNavClick}>
          Email Templates
        </NavLink>
        <NavLink to="/rewards" icon={<TrophyIcon />} onClick={onNavClick}>
          Rewards
        </NavLink>

        {/* My Commissions - Only visible to sales reps and sales managers (not admins) */}
        {(user?.salesRepId || user?.userType === 'sales_rep' || user?.userType === 'sales_manager') && (
          <NavLink to="/my-commissions" icon={<CommissionIcon />} onClick={onNavClick}>
            My Commissions
          </NavLink>
        )}

        {/* Warranty Menu */}
        <div className="pt-2">
          <button
            onClick={() => setWarrantyMenuOpen(!warrantyMenuOpen)}
            className={clsx(
              'flex items-center justify-between w-full px-4 py-2 rounded-lg transition-colors min-h-[44px]',
              location.pathname.startsWith('/warranty')
                ? 'bg-keder-yellow/20 text-keder-yellow'
                : 'text-[var(--sidebar-text)] hover:bg-[var(--bg-hover)] hover:text-[var(--sidebar-text-hover)]'
            )}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5"><WarrantyIcon /></span>
              <span className="nav-text">Warranty</span>
            </div>
            <span className={clsx('transition-transform', warrantyMenuOpen && 'rotate-180')}>
              <ChevronDownIcon />
            </span>
          </button>
          {warrantyMenuOpen && (
            <div className="ml-8 mt-1 space-y-1">
              <NavLink to="/warranty/terms" icon={null} onClick={onNavClick}>
                Warranty Terms & Documents
              </NavLink>
              <NavLink to="/warranty/register" icon={null} onClick={onNavClick}>
                Register Warranty
              </NavLink>
              <NavLink to="/warranty/my-warranties" icon={null} onClick={onNavClick}>
                My Warranties
              </NavLink>
              <NavLink to="/warranty/submit-rma" icon={null} onClick={onNavClick}>
                Submit RMA
              </NavLink>
              <NavLink to="/warranty/my-rmas" icon={null} onClick={onNavClick}>
                My RMAs
              </NavLink>
            </div>
          )}
        </div>

        {/* Admin Menu - Only visible to dealer_admin */}
        {user?.userType === 'dealer_admin' && (
          <div className="pt-2">
            <button
              onClick={() => setAdminMenuOpen(!adminMenuOpen)}
              className={clsx(
                'flex items-center justify-between w-full px-4 py-2 rounded-lg transition-colors min-h-[44px]',
                location.pathname.startsWith('/admin')
                  ? 'bg-keder-yellow/20 text-keder-yellow'
                  : 'text-[var(--sidebar-text)] hover:bg-[var(--bg-hover)] hover:text-[var(--sidebar-text-hover)]'
              )}
            >
              <div className="flex items-center gap-3">
                <span className="w-5 h-5"><AdminIcon /></span>
                <span className="nav-text">Admin</span>
              </div>
              <span className={clsx('transition-transform', adminMenuOpen && 'rotate-180')}>
                <ChevronDownIcon />
              </span>
            </button>
            {adminMenuOpen && (
              <div className="ml-8 mt-1 space-y-1">
                <NavLink to="/admin/team" icon={null} onClick={onNavClick}>
                  Team Management
                </NavLink>
                <NavLink to="/admin/commission-settings" icon={null} onClick={onNavClick}>
                  Commission Settings
                </NavLink>
                <NavLink to="/admin/commission-reports" icon={null} onClick={onNavClick}>
                  Commission Reports
                </NavLink>
                <NavLink to="/admin/sales-overview" icon={null} onClick={onNavClick}>
                  Sales Overview
                </NavLink>
                <NavLink to="/admin/activity-log" icon={null} onClick={onNavClick}>
                  Activity Log
                </NavLink>
              </div>
            )}
          </div>
        )}

        {/* Support Menu */}
        <div className="pt-2">
          <button
            onClick={() => setSupportMenuOpen(!supportMenuOpen)}
            className={clsx(
              'flex items-center justify-between w-full px-4 py-2 rounded-lg transition-colors min-h-[44px]',
              location.pathname.startsWith('/support') || location.pathname === '/install-resources' || location.pathname === '/cad-drawings' || location.pathname === '/marketing-resources'
                ? 'bg-keder-yellow/20 text-keder-yellow'
                : 'text-[var(--sidebar-text)] hover:bg-[var(--bg-hover)] hover:text-[var(--sidebar-text-hover)]'
            )}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5"><SupportIcon /></span>
              <span className="nav-text">Support</span>
              {!supportMenuOpen && supportUnread ? (
                <span title="A reply from Keder you haven't opened" className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-keder-yellow text-black animate-pulse">{supportUnread}</span>
              ) : null}
            </div>
            <span className={clsx('transition-transform', supportMenuOpen && 'rotate-180')}>
              <ChevronDownIcon />
            </span>
          </button>
          {supportMenuOpen && (
            <div className="ml-8 mt-1 space-y-1">
              <NavLink to="/support" icon={null} onClick={onNavClick}>
                <span className="flex items-center gap-2">Dealer Support{supportUnread ? <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-keder-yellow text-black">{supportUnread}</span> : null}</span>
              </NavLink>
              <NavLink to="/install-resources" icon={null} onClick={onNavClick}>
                Install Resources
              </NavLink>
              <NavLink to="/cad-drawings" icon={null} onClick={onNavClick}>
                V-Track CAD Drawings
              </NavLink>
              <NavLink to="/marketing-resources" icon={null} onClick={onNavClick}>
                Marketing Resources
              </NavLink>
            </div>
          )}
        </div>

        <NavLink to="/profile" icon={<SettingsIcon />} onClick={onNavClick}>
          Profile
        </NavLink>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-[var(--border-color)]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-keder-yellow text-black flex items-center justify-center font-bold flex-shrink-0">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user?.name}</p>
            <p className="text-xs text-[var(--text-secondary)] truncate">{user?.dealerName}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full text-left text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors min-h-[44px] flex items-center"
        >
          Sign out
        </button>
      </div>
    </>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [warrantyMenuOpen, setWarrantyMenuOpen] = useState(
    location.pathname.startsWith('/warranty')
  );
  const [adminMenuOpen, setAdminMenuOpen] = useState(
    location.pathname.startsWith('/admin')
  );
  const [supportMenuOpen, setSupportMenuOpen] = useState(
    location.pathname.startsWith('/support') || location.pathname === '/install-resources' || location.pathname === '/cad-drawings' || location.pathname === '/marketing-resources'
  );
  const [supportUnread, setSupportUnread] = useState(0);

  // Support flasher: tickets with a reply from Keder the dealer hasn't opened.
  // Refetches on route change so opening a ticket (which marks it viewed) clears it.
  useEffect(() => {
    api.getSupportUnreadCount().then(setSupportUnread).catch(() => {});
  }, [location.pathname]);

  // Close mobile menu and scroll to top when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Initialize the dark/light indicator from the persisted preference (theme.ts
  // already applied it flash-free in main.tsx). A preset like Slate/Ocean is a
  // dark base, so the sun/moon reflects the base mode.
  useEffect(() => {
    setIsDarkMode(resolveMode(loadThemePref()) === 'dark');

    // Reconcile with the account copy so the theme follows the user across
    // devices. DB wins; cache locally + re-apply if it differs from boot.
    api.getUiTheme().then((remote) => {
      if (!remote) return;
      const pref = normalizePref(remote);
      saveThemePrefLocal(pref);
      setIsDarkMode(resolveMode(pref) === 'dark');
    }).catch(() => {});
  }, []);

  // Top-bar sun/moon = flip to the OPPOSITE mode, restoring the theme you last
  // used in that mode (falls back to Keder default only if you've never picked
  // one). Never discards a chosen preset/custom theme.
  const toggleTheme = () => {
    const next = oppositeModePref(loadThemePref());
    setIsDarkMode(resolveMode(next) === 'dark');
    saveThemePrefLocal(next);
    api.updateUiTheme(next).catch(() => {});
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[var(--bg-page)] flex flex-col md:flex-row">
      {/* Mobile Header */}
      <header className="md:hidden h-14 bg-[var(--bg-sidebar)] border-b border-[var(--border-color)] flex items-center justify-between px-4 sticky top-0 z-30">
        {/* Hamburger Button */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-[var(--text-primary)]"
          aria-label="Open menu"
        >
          <HamburgerIcon />
        </button>

        {/* Logo — dealer white-label or Keder default */}
        <img
          src={user?.logoUrl || KEDER_LOGO_URL}
          alt={user?.dealerName || 'Keder Screens'}
          style={{ maxHeight: '44px', maxWidth: '170px', width: 'auto', objectFit: 'contain' }}
          onError={(e) => { (e.target as HTMLImageElement).src = KEDER_LOGO_URL; }}
        />

        {/* User Avatar */}
        <div className="w-8 h-8 rounded-full bg-keder-yellow text-black flex items-center justify-center font-bold text-sm">
          {user?.name?.charAt(0) || 'U'}
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div
        className={clsx('sidebar-overlay md:hidden', mobileMenuOpen && 'open')}
        onClick={closeMobileMenu}
      />

      {/* Mobile Sidebar */}
      <aside
        className={clsx(
          'mobile-sidebar md:hidden bg-[var(--bg-sidebar)] flex flex-col',
          mobileMenuOpen && 'open'
        )}
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
          <span className="text-[var(--text-primary)] font-semibold">Menu</span>
          <button
            onClick={closeMobileMenu}
            className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-[var(--text-primary)]"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        <SidebarContent
          user={user}
          warrantyMenuOpen={warrantyMenuOpen}
          setWarrantyMenuOpen={setWarrantyMenuOpen}
          adminMenuOpen={adminMenuOpen}
          setAdminMenuOpen={setAdminMenuOpen}
          supportMenuOpen={supportMenuOpen}
          setSupportMenuOpen={setSupportMenuOpen}
          supportUnread={supportUnread}
          onNavClick={closeMobileMenu}
          handleLogout={handleLogout}
        />
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 lg:w-64 bg-[var(--bg-sidebar)] border-r border-[var(--border-color)] flex-col flex-shrink-0">
        <SidebarContent
          user={user}
          warrantyMenuOpen={warrantyMenuOpen}
          setWarrantyMenuOpen={setWarrantyMenuOpen}
          adminMenuOpen={adminMenuOpen}
          setAdminMenuOpen={setAdminMenuOpen}
          supportMenuOpen={supportMenuOpen}
          setSupportMenuOpen={setSupportMenuOpen}
          supportUnread={supportUnread}
          handleLogout={handleLogout}
        />
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto flex flex-col min-w-0">
        <SystemBanner />
        <PromoBanner />
        <RepFirmBanner />
        {/* Desktop Top Header Bar */}
        <header className="hidden md:flex h-14 bg-[var(--bg-header)] border-b border-[var(--border-color)] items-center justify-end px-6 shrink-0">
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-[var(--text-secondary)] hover:text-keder-yellow"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-medium text-[var(--text-primary)]">{user?.name}</p>
                <p className="text-xs text-[var(--text-secondary)]">{user?.dealerName || user?.companyName}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-keder-yellow text-black flex items-center justify-center font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">{children}</div>

        {/* Footer */}
        <footer className="py-4 px-6 border-t border-[var(--border-color)] text-center text-xs text-[var(--text-muted)]">
          {user?.logoUrl ? (
            <>
              &copy; {new Date().getFullYear()} {user?.dealerName || user?.companyName} | Powered by <a href="https://kederscreens.com" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-secondary)]">Keder Screens</a>
            </>
          ) : (
            <>
              &copy; {new Date().getFullYear()} Keder Screens | <a href="https://kederscreens.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-secondary)]">Terms</a> | <a href="https://kederscreens.com/privacy/" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-secondary)]">Privacy</a> | <a href="https://kederscreens.com/cookie-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-secondary)]">Cookies</a>
            </>
          )}
        </footer>
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[var(--text-primary)]">{title}</h1>
        {subtitle && <p className="text-[var(--text-secondary)] mt-1 text-sm md:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-3 w-full sm:w-auto">{actions}</div>}
    </div>
  );
}

export function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={clsx('animate-spin rounded-full border-2 border-keder-gray border-t-keder-yellow', sizeClasses[size])} />
  );
}

export function LoadingPage() {
  return (
    <div className="min-h-screen bg-[var(--bg-page)] flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}
